import {spring} from 'motion';
import {animate} from 'motion/mini';

import {planLogoBatch, quoteAutoplayRuns, quoteClockAfterChange, releaseQuoteNavHold} from './logoWall.util.js';

const logoInterval = 1500;
const quoteInterval = 9000;

/** Coordinated logo swaps and a separate quote carousel; no merging tiles. */
class LogoWall extends HTMLElement {
    connectedCallback() {
        this.abort = new AbortController();
        this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
        this.logos = [...this.querySelectorAll('[data-logo]')].map((logo) => ({
            id: logo.dataset.logo, persistent: logo.hasAttribute('data-logo-persistent'), template: logo.cloneNode(true),
        })).sort((a, b) => Number(a.id) - Number(b.id));
        this.cells = [...this.querySelectorAll('[data-logo-cell]')].map((cell) => {
            const current = cell.querySelector('[data-logo]:not([hidden])');
            cell.querySelectorAll('[data-logo][hidden]').forEach((logo) => logo.remove());
            return {track: cell.querySelector('[data-logo-track]'), logo: current?.dataset.logo, busy: false};
        });
        this.logoTurn = this.cells.length;
        this.logoStep = 0;
        this.logoBusy = false;
        this.quotes = {items: [...this.querySelectorAll('[data-quote]')], index: 0, visibleIndex: 0, busy: false};
        this.steps = [...this.querySelectorAll('[data-quote-step]')];
        this.quoteNavIgnoreHover = false;
        this.quoteNavIgnoreFocus = false;
        this.timers = new Set();
        this.animations = new Set();
        this.visible = false;
        this.logoInteraction = {hovered: false, focused: false};
        this.quoteInteraction = {hovered: false, focused: false};
        const styles = getComputedStyle(this);
        this.transition = {
            duration: parseFloat(styles.getPropertyValue('--site-entry-duration')) / 1000,
            ease: styles.getPropertyValue('--site-entry-ease'),
        };
        this.fadeDuration = parseFloat(styles.getPropertyValue('--site-duration')) / 1000;
        const options = {signal: this.abort.signal};
        const refresh = () => this.refresh();
        this.reduced.addEventListener('change', () => {
            if (this.reduced.matches) this.animations.forEach((animation) => animation.complete());
            refresh();
        }, options);
        document.addEventListener('visibilitychange', refresh, options);
        for (const [selector, kind] of [['.logoWall__grid', 'logo'], ['.logoWall__testimonials', 'quote']]) {
            const region = this.querySelector(selector);
            if (!region) continue;
            const interaction = this[`${kind}Interaction`];
            region.addEventListener('pointerenter', (event) => {
                if (event.pointerType !== 'mouse') return;
                interaction.hovered = true;
                this.refresh(kind);
            }, options);
            region.addEventListener('pointerleave', () => {
                interaction.hovered = false;
                this.refresh(kind);
            }, options);
            region.addEventListener('focusin', () => {
                interaction.focused = true;
                this.refresh(kind);
            }, options);
            region.addEventListener('focusout', (event) => {
                interaction.focused = region.contains(event.relatedTarget);
                this.refresh(kind);
            }, options);
        }
        for (const [selector, direction] of [['[data-prev]', -1], ['[data-next]', 1]]) {
            this.querySelector(selector)?.addEventListener('click', (event) => {
                this.changeQuote(direction, event.detail === 0);
            }, options);
        }
        this.steps.forEach((step, index) => {
            step.addEventListener('click', (event) => {
                if (index !== this.quotes.index) this.changeQuote(index - this.quotes.index, event.detail === 0);
            }, options);
        });
        const steps = this.querySelector('[data-quote-steps]');
        if (steps) steps.hidden = false;
        const controls = this.querySelector('[data-controls]');
        this.caseStudy = this.querySelector('[data-case-study]');
        if (controls) controls.hidden = this.quotes.items.length < 2 && !this.caseStudy?.getAttribute('href');
        this.intersection = new IntersectionObserver(([entry]) => {
            this.visible = entry.isIntersecting;
            refresh();
        }, {threshold: 0.15});
        this.intersection.observe(this);
    }

    get running() {
        return this.isConnected && this.visible && !document.hidden && !this.reduced.matches;
    }

    get logosRunning() {
        return this.running && !this.logoInteraction.hovered && !this.logoInteraction.focused;
    }

    get quotesRunning() {
        return quoteAutoplayRuns({
            running: this.running,
            hovered: this.quoteInteraction.hovered,
            focused: this.quoteInteraction.focused,
            ignoreHover: this.quoteNavIgnoreHover,
            ignoreFocus: this.quoteNavIgnoreFocus,
        });
    }

    later(callback, delay) {
        const timer = setTimeout(() => {
            this.timers.delete(timer);
            if (this.running) callback();
        }, delay);
        this.timers.add(timer);
        return timer;
    }

    refresh(kind) {
        if (!kind || kind === 'logo') {
            clearTimeout(this.logoTimer);
            this.timers.delete(this.logoTimer);
            this.scheduleLogo();
        }
        if (!kind || kind === 'quote') {
            // A click's hover and focus stop counting as a pause once they end.
            const hold = releaseQuoteNavHold({
                ignoreHover: this.quoteNavIgnoreHover,
                ignoreFocus: this.quoteNavIgnoreFocus,
                hovered: this.quoteInteraction.hovered,
                focused: this.quoteInteraction.focused,
            });
            this.quoteNavIgnoreHover = hold.ignoreHover;
            this.quoteNavIgnoreFocus = hold.ignoreFocus;
            this.scheduleQuote();
        }
    }

    scheduleLogo(delay = logoInterval) {
        if (this.logos.length < 2 || this.logoBusy || !this.logosRunning) return;
        clearTimeout(this.logoTimer);
        this.timers.delete(this.logoTimer);
        this.logoTimer = this.later(async () => {
            const started = performance.now();
            const filledCells = this.cells.filter((cell) => cell.logo !== undefined);
            if (!filledCells.length) return;
            const cell = filledCells[this.logoStep % filledCells.length];
            const index = this.cells.indexOf(cell);
            this.logoStep++;
            const plan = planLogoBatch(this.cells, this.logos, index, this.logoTurn);
            if (!plan.length) {
                this.scheduleLogo();
                return;
            }
            this.logoBusy = true;
            const changes = plan.map(({index: destination, logo}) => {
                const target = this.cells[destination];
                target.busy = true;
                target.incoming = logo;
                return {cell: target, logo, incoming: this.logos.find((item) => item.id === logo).template.cloneNode(true)};
            });
            this.logoTurn = (this.logos.findIndex((logo) => logo.id === plan[0].logo) + 1) % this.logos.length;
            const images = changes.flatMap(({incoming}) => [...incoming.querySelectorAll('img')]);
            images.forEach((img) => {
                img.loading = 'eager';
            });
            await Promise.all(images.map((img) => img.decode().catch(() => {})));
            if (this.logosRunning) {
                await this.pushLogos(changes);
                changes.forEach((change) => {
                    change.cell.logo = change.logo;
                });
            }
            changes.forEach((change) => {
                change.cell.busy = false;
                change.cell.incoming = undefined;
            });
            this.logoBusy = false;
            // Keep a start-to-start beat while allowing slow images to finish safely.
            this.scheduleLogo(Math.max(0, logoInterval - (performance.now() - started)));
        }, delay);
    }

    async pushLogos(changes) {
        changes.forEach(({cell, incoming}) => {
            incoming.style.transform = 'translateY(100%)';
            incoming.hidden = false;
            incoming.inert = true;
            cell.track.firstElementChild.inert = true;
            cell.track.append(incoming);
        });
        // Both ends of a swap share the same spring and start together.
        const animation = animate(changes.map(({cell}) => cell.track), {
            transform: ['translateY(0%)', 'translateY(-100%)'],
        }, this.reduced.matches ? {duration: 0} : {type: spring, duration: 0.5, bounce: 0.2});
        this.animations.add(animation);
        await animation;
        this.animations.delete(animation);
        // Rebase every incoming logo in the same frame, leaving one per cell.
        changes.forEach(({cell, incoming}) => {
            cell.track.firstElementChild.remove();
            incoming.inert = false;
            incoming.style.removeProperty('transform');
            cell.track.style.removeProperty('transform');
        });
    }

    scheduleQuote() {
        if (this.quotes.items.length < 2) return;
        if (!this.quoteProgress) {
            const fill = this.steps[this.quotes.index].querySelector('.logoWall__stepFill');
            // A quote change calls resetQuoteProgress first, so this clock starts at zero.
            const progress = animate(fill, {transform: ['scaleX(0)', 'scaleX(1)']}, {
                duration: quoteInterval / 1000, ease: 'linear', autoplay: false,
            });
            this.quoteProgress = progress;
            progress.then(() => {
                if (this.quoteProgress === progress && this.quotesRunning) this.changeQuote(1, false, true);
            });
        }
        if (this.quotesRunning && !this.quotes.busy) this.quoteProgress.play();
        else this.quoteProgress.pause();
    }

    resetQuoteProgress() {
        const progress = this.quoteProgress;
        // Drop the reference before stop so a finished animation cannot advance again.
        this.quoteProgress = null;
        progress?.stop();
        this.steps.forEach((step) => {
            step.querySelector('.logoWall__stepFill').style.removeProperty('transform');
        });
    }

    updateQuoteSteps(instant = false) {
        // Measure the current visual positions so rapid selections retarget smoothly.
        const previous = this.steps.map((step) => ({
            center: step.getBoundingClientRect().left + step.getBoundingClientRect().width / 2,
            clip: getComputedStyle(step.querySelector('.logoWall__stepTrack')).clipPath,
        }));
        this.stepAnimations?.forEach((animation) => {
            animation.stop();
            this.animations.delete(animation);
        });
        this.steps.forEach((step, index) => {
            step.style.removeProperty('transform');
            step.querySelector('.logoWall__stepTrack').style.removeProperty('clip-path');
            if (index === this.quotes.index) step.setAttribute('aria-current', 'true');
            else step.removeAttribute('aria-current');
        });
        if (instant || this.reduced.matches) return;
        const positions = this.steps.map((step) => {
            const rect = step.getBoundingClientRect();
            return rect.left + rect.width / 2;
        });
        this.stepAnimations = this.steps.flatMap((step, index) => {
            const track = step.querySelector('.logoWall__stepTrack');
            const springOptions = {type: spring, duration: 0.5, bounce: 0.2};
            return [
                animate(step, {transform: [`translateX(${previous[index].center - positions[index]}px)`, 'translateX(0px)']}, springOptions),
                animate(track, {clipPath: [previous[index].clip, getComputedStyle(track).clipPath]}, springOptions),
            ];
        });
        this.stepAnimations.forEach((animation) => {
            this.animations.add(animation);
            animation.then(() => this.animations.delete(animation));
        });
    }

    async changeQuote(direction, instant = false, automatic = false) {
        const state = this.quotes;
        if (state.items.length < 2) return;
        const hold = quoteClockAfterChange({
            automatic,
            hovered: this.quoteInteraction.hovered,
            focused: this.quoteInteraction.focused,
            ignoreHover: this.quoteNavIgnoreHover,
            ignoreFocus: this.quoteNavIgnoreFocus,
        });
        this.quoteNavIgnoreHover = hold.ignoreHover;
        this.quoteNavIgnoreFocus = hold.ignoreFocus;
        if (hold.restart) this.resetQuoteProgress();
        this.quoteTransition?.abort();
        const transition = new AbortController();
        this.quoteTransition = transition;
        // Advance intent immediately, so every click counts even during an exit.
        state.index = (state.index + direction + state.items.length) % state.items.length;
        this.updateQuoteSteps(instant);
        state.busy = true;
        this.scheduleQuote();
        this.caseStudy.inert = true;
        await this.swapQuote(state.items[state.visibleIndex], state.items[state.index], instant, transition.signal);
        if (transition.signal.aborted) return;
        state.busy = false;
        this.scheduleQuote();
    }

    updateCaseStudy(quote) {
        const {caseStudyUrl, company} = quote.dataset;
        this.caseStudy.hidden = !caseStudyUrl;
        if (caseStudyUrl) this.caseStudy.setAttribute('href', caseStudyUrl);
        else this.caseStudy.removeAttribute('href');
        this.caseStudy.setAttribute('aria-label', `Read more about ${company}`);
        this.caseStudy.inert = false;
    }

    async swapQuote(outgoing, incoming, instant, signal) {
        const oldQuote = outgoing.querySelector('blockquote');
        const oldAttribution = [...outgoing.querySelectorAll('figcaption, .logoWall__quoteLogo')];
        const newQuote = incoming.querySelector('blockquote');
        const newAttribution = [...incoming.querySelectorAll('figcaption, .logoWall__quoteLogo')];
        outgoing.inert = true;

        if (outgoing !== incoming) {
            // Retarget from the current visual state rather than restarting at 100%.
            if (!instant && !this.reduced.matches) {
                await this.playQuoteAnimations([
                    animate(oldQuote, {opacity: 0, transform: 'translateY(-8px)'}, {...this.transition, duration: this.fadeDuration}),
                    animate(oldAttribution, {opacity: 0}, {...this.transition, duration: this.fadeDuration}),
                ], signal);
                if (signal.aborted) return;
            }
            outgoing.setAttribute('aria-hidden', 'true');
            for (const element of [oldQuote, ...oldAttribution]) {
                element.style.removeProperty('opacity');
                element.style.removeProperty('transform');
            }
            newQuote.style.opacity = '0';
            newQuote.style.transform = 'translateY(16px)';
            newAttribution.forEach((element) => {
                element.style.opacity = '0';
            });
        }
        incoming.removeAttribute('aria-hidden');
        incoming.inert = false;
        this.quotes.visibleIndex = this.quotes.index;
        this.updateCaseStudy(incoming);

        if (!instant && !this.reduced.matches && this.isConnected) {
            await this.playQuoteAnimations([
                animate(newQuote, {opacity: 1, transform: 'translateY(0px)'}, this.transition),
                animate(newAttribution, {opacity: 1}, {...this.transition, duration: this.fadeDuration, delay: outgoing === incoming ? 0 : 0.12}),
            ], signal);
            if (signal.aborted) return;
        }
        for (const element of [newQuote, ...newAttribution]) {
            element.style.removeProperty('opacity');
            element.style.removeProperty('transform');
        }
    }

    async playQuoteAnimations(animations, signal) {
        animations.forEach((animation) => this.animations.add(animation));
        let interrupt;
        const interrupted = new Promise((resolve) => {
            interrupt = () => {
                // Motion commits the current styles before stopping, avoiding a jump.
                animations.forEach((animation) => animation.stop());
                resolve();
            };
            signal.addEventListener('abort', interrupt, {once: true});
        });
        await Promise.race([Promise.all(animations), interrupted]);
        signal.removeEventListener('abort', interrupt);
        animations.forEach((animation) => this.animations.delete(animation));
    }

    disconnectedCallback() {
        this.resetQuoteProgress();
        this.quoteTransition?.abort();
        this.abort.abort();
        this.intersection.disconnect();
        this.timers.forEach(clearTimeout);
        this.animations.forEach((animation) => animation.complete());
    }
}

customElements.define('fk-logo-wall', LogoWall);
